/**
 * A tab older than the build it is talking to reloads instead of breaking.
 *
 * Vite dispatches `vite:preloadError` when a dynamic import rejects, whatever
 * the reason. Reloading fetches the current index and the app comes back on
 * the version actually deployed.
 *
 * It reloads in one situation and declines in four, because a reload throws
 * away everything unsaved on the page:
 *
 * - offline: the import failed because the network did, and reloading lands on
 *   the browser's offline page while spending a recovery attempt;
 * - the failure arrives seconds into a page that is ITSELF a recovery reload,
 *   which means reloading did not fix it;
 * - the tab has already spent its attempts, which is how a permanently broken
 *   deploy stops being a reload every minute of use;
 * - the attempt cannot be remembered, because site data is blocked -- common
 *   in the in-app browsers a WhatsApp audience arrives through. A write that
 *   silently does nothing would leave every failure reloading forever.
 *
 * Why this is the guarantee rather than the grace, and the layers around it:
 * `docs/adr/0029-a-stale-tab-reloads-itself.md`.
 */

const ATTEMPTS = 'fluxvia:stale-build-attempts';
const RETRY_WINDOW_MS = 60_000;
const MAX_ATTEMPTS = 3;
const FORGET_AFTER_MS = 60 * 60 * 1_000;

type Attempts = { at: number; n: number };

function spent(): Attempts | null {
  try {
    const raw = window.sessionStorage.getItem(ATTEMPTS);
    if (raw === null) {
      return null;
    }
    const { at, n } = JSON.parse(raw) as Partial<Attempts>;
    return Number.isFinite(at) && Number.isFinite(n) ? { at: at as number, n: n as number } : null;
  } catch {
    return null;
  }
}

/**
 * Whether this page is itself the product of a recovery reload.
 *
 * The stamp is written immediately before reloading, so on the page that
 * follows it sits just behind `timeOrigin`. Asking THAT, rather than how long
 * ago the stamp was written, is what lets the same tab recover from a second
 * deploy hours later: an earlier draft compared the stamp to a value frozen at
 * page load, which is constant for the life of the page, so a tab born of one
 * reload could never recover again.
 */
function cameFromRecovery(last: Attempts | null): boolean {
  return last !== null && performance.timeOrigin - last.at < RETRY_WINDOW_MS;
}

/**
 * How many reloads this tab has already spent on a run of failures.
 *
 * Attempts older than an hour are forgotten, so a tab left open across a week
 * of deploys keeps recovering while a build whose chunks are permanently gone
 * stops after three. Without the count the guard above lapses sixty seconds
 * into each recovered page, which is a reload per minute of use, each one
 * discarding whatever was typed.
 */
function alreadySpent(last: Attempts | null, now: number): number {
  if (last === null || now - last.at > FORGET_AFTER_MS) {
    return 0;
  }
  return last.n;
}

/** Whether the attempt is readable back, which is the whole question. */
function remember(next: Attempts): boolean {
  const value = JSON.stringify(next);
  try {
    window.sessionStorage.setItem(ATTEMPTS, value);
    return window.sessionStorage.getItem(ATTEMPTS) === value;
  } catch {
    return false;
  }
}

/**
 * Reload when a chunk this build asks for is no longer being served.
 *
 * Refusing to act costs the screen: there is no error boundary above the lazy
 * routes yet, so a propagated import error unmounts the React root. It is
 * still the right answer in the four cases the module docstring lists, and
 * making it degrade instead is owed (ADR-0029).
 */
export function recoverFromStaleBuild(): void {
  window.addEventListener('vite:preloadError', event => {
    if (!navigator.onLine) {
      return;
    }
    const last = spent();
    if (cameFromRecovery(last) && performance.now() < RETRY_WINDOW_MS) {
      return;
    }
    const now = Date.now();
    const n = alreadySpent(last, now);
    if (n >= MAX_ATTEMPTS) {
      return;
    }
    if (!remember({ at: now, n: n + 1 })) {
      return;
    }
    event.preventDefault();
    window.location.reload();
  });
}
